import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";

// Owner-only page: what the business owes Paula + labor watch.
// Backend enforces requireRole("owner"); this page is also hidden from the
// sidebar for everyone else. No banking details anywhere by design.

interface Draw { date: string; paid: boolean }
interface Entry { id: string; entry_date: string; entry_type: string; amount: string; note: string | null }
interface Summary {
  balance: number; loan_seed: number; arrears_seed: number; arrears_paid: number;
  arrears_remaining: number; weekly_draw: number; resume_date: string;
  draws: Draw[]; missed_ongoing: number; entries: Entry[];
}
interface LaborWeek { week_start: string; hours: string; cost: string; staff: number; product_sales: string; labor_pct: string | null }

const fmt = (n: number | string) => Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function OwnerLedgerPage() {
  const { user } = useAuth();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [labor, setLabor] = useState<LaborWeek[]>([]);
  const [error, setError] = useState("");
  const [payAmount, setPayAmount] = useState("500");
  const [payNote, setPayNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/owner-ledger/summary"), api.get("/owner-ledger/labor")]);
      setSummary(s.data); setLabor(l.data);
    } catch { setError("Could not load (owner only)."); }
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "owner") return <div className="p-8">Owner only.</div>;
  if (error) return <div className="p-8">{error}</div>;
  if (!summary) return <div className="p-8">Loading...</div>;

  const addEntry = async (entry_type: string, amount: number, entry_date: string, note: string) => {
    setBusy(true);
    try { await api.post("/owner-ledger/entries", { entry_type, amount, entry_date, note }); await load(); }
    catch { setError("Failed to save entry."); }
    finally { setBusy(false); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: "var(--rbw-navy)" }}>Owner Ledger</h1>

      {/* Balance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl p-4" style={{ background: "var(--rbw-navy)", color: "#fff" }}>
          <div className="text-xs uppercase tracking-widest opacity-70">Owed to Paula</div>
          <div className="text-3xl font-bold mt-1">{fmt(summary.balance)}</div>
          <div className="text-xs opacity-70 mt-1">
            {fmt(summary.loan_seed)} loan + {fmt(summary.arrears_seed)} missed draws
            {summary.missed_ongoing > 0 && ` + ${summary.missed_ongoing} missed since restart`}
          </div>
        </div>
        <div className="rounded-xl p-4 bg-white" style={{ border: "1px solid var(--rbw-border)" }}>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--rbw-gray-dark)" }}>Paid back so far</div>
          <div className="text-3xl font-bold mt-1" style={{ color: "var(--rbw-navy)" }}>{fmt(summary.arrears_paid)}</div>
          <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>at $500/wk you're whole in ~{Math.max(1, Math.ceil(summary.arrears_remaining / 500))} weeks</div>
        </div>
        <div className="rounded-xl p-4 bg-white" style={{ border: "1px solid var(--rbw-border)" }}>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--rbw-gray-dark)" }}>Weekly draw</div>
          <div className="text-3xl font-bold mt-1" style={{ color: "var(--rbw-navy)" }}>{fmt(summary.weekly_draw)}</div>
          <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>every Thursday, restarted {summary.resume_date}</div>
        </div>
      </div>

      {/* Thursday draw tracker */}
      <div className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--rbw-border)" }}>
        <div className="font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>Thursday draws since restart</div>
        {summary.draws.length === 0 && <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>First draw comes due {summary.resume_date}.</div>}
        <div className="flex flex-wrap gap-2">
          {summary.draws.map((d) => (
            <div key={d.date} className="px-3 py-2 rounded-lg text-sm flex items-center gap-2"
              style={{ background: d.paid ? "#ECFDF5" : "#FEF2F2", border: `1px solid ${d.paid ? "#A7F3D0" : "#FECACA"}`, color: "var(--rbw-navy)" }}>
              {d.date} {d.paid ? "✓ paid" : "MISSED"}
              {!d.paid && (
                <button disabled={busy} className="underline text-xs"
                  onClick={() => addEntry("draw_paid", summary.weekly_draw, d.date, "Weekly $750 draw")}>
                  mark paid
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Record arrears payment */}
      <div className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--rbw-border)" }}>
        <div className="font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>Record a payback payment</div>
        <div className="flex flex-wrap items-center gap-2">
          <span style={{ color: "var(--rbw-gray-dark)" }}>$</span>
          <input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)}
            className="w-28 px-2 py-1 rounded-lg" style={{ border: "2px solid var(--rbw-border)" }} />
          <input type="text" placeholder="note (optional)" value={payNote} onChange={(e) => setPayNote(e.target.value)}
            className="w-64 px-2 py-1 rounded-lg" style={{ border: "2px solid var(--rbw-border)" }} />
          <button disabled={busy || !Number(payAmount)}
            className="px-4 py-1.5 rounded-lg text-white font-semibold" style={{ background: "var(--rbw-navy)" }}
            onClick={() => { addEntry("arrears_payment", Number(payAmount), today, payNote || "Arrears payback"); setPayNote(""); }}>
            Record
          </button>
        </div>
      </div>

      {/* Labor watch */}
      <div className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--rbw-border)" }}>
        <div className="font-semibold mb-1" style={{ color: "var(--rbw-navy)" }}>Labor watch (scheduled hours vs sales)</div>
        <div className="text-xs mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
          Hourly staff only. Labor % = scheduled cost / that week's product sales. Future weeks have no sales yet.
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: "var(--rbw-gray-dark)" }} className="text-left">
                <th className="py-1 pr-4">Week</th><th className="py-1 pr-4">Hours</th><th className="py-1 pr-4">Cost</th>
                <th className="py-1 pr-4">Staff</th><th className="py-1 pr-4">Sales</th><th className="py-1">Labor %</th>
              </tr>
            </thead>
            <tbody>
              {labor.map((w) => (
                <tr key={w.week_start} style={{ borderTop: "1px solid var(--rbw-border)", color: "var(--rbw-navy)" }}>
                  <td className="py-1.5 pr-4">{w.week_start}</td>
                  <td className="py-1.5 pr-4">{Number(w.hours)}</td>
                  <td className="py-1.5 pr-4">{fmt(w.cost)}</td>
                  <td className="py-1.5 pr-4">{w.staff}</td>
                  <td className="py-1.5 pr-4">{Number(w.product_sales) > 0 ? fmt(w.product_sales) : "—"}</td>
                  <td className="py-1.5 font-semibold" style={{ color: w.labor_pct && Number(w.labor_pct) > 16 ? "#B91C1C" : "var(--rbw-navy)" }}>
                    {w.labor_pct ? `${w.labor_pct}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* History */}
      <div className="rounded-xl bg-white p-4" style={{ border: "1px solid var(--rbw-border)" }}>
        <div className="font-semibold mb-2" style={{ color: "var(--rbw-navy)" }}>Ledger history</div>
        <div style={{ overflowX: "auto" }}>
          <table className="w-full text-sm">
            <tbody>
              {summary.entries.map((e) => (
                <tr key={e.id} style={{ borderTop: "1px solid var(--rbw-border)", color: "var(--rbw-navy)" }}>
                  <td className="py-1.5 pr-4 whitespace-nowrap">{e.entry_date}</td>
                  <td className="py-1.5 pr-4">{e.entry_type.replace(/_/g, " ")}</td>
                  <td className="py-1.5 pr-4 font-semibold">{fmt(e.amount)}</td>
                  <td className="py-1.5 pr-4 text-xs" style={{ color: "var(--rbw-gray-dark)" }}>{e.note}</td>
                  <td className="py-1.5 text-right">
                    {e.entry_type !== "loan_seed" && e.entry_type !== "arrears_seed" && (
                      <button disabled={busy} className="text-xs underline"
                        onClick={async () => { setBusy(true); try { await api.delete(`/owner-ledger/entries/${e.id}`); await load(); } finally { setBusy(false); } }}>
                        remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
